import React, { useState, useCallback, useRef } from "react";
import { Upload, CheckCircle, Eye, X, ArrowLeft, ArrowRight, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { mo3tiThemes } from "../../../../../constant/theme";
import Mo3tiButton from "../../layout/Mo3tiButton";
import { apiFetch } from "../../../../../context/AuthContext";
import UploadStep from "./UploadStep";
import PreviewStep from "./PreviewStep";
import ResultsStep from "./ResultsStep";
import SensitiveOverwriteDialog from "./SensitiveOverwriteDialog";
import {
  PERSONAL_BY_HEADER,
  VALUE_BY_HEADER,
  PAYROLL_IGNORED_HEADERS,
  isAllowanceDeductionHeader,
} from "../../../constants/payrollImportColumns";

const ExcelImportModal = ({
  onClose,
  onImportComplete,
  API_BASE_URL,
  t,
  language,
  theme,
  isRTL,
  importStep,
  setImportStep,
  importFile,
  setImportFile,
  importData,
  setImportData,
  importLoading,
  setImportLoading,
  importError,
  setImportError,
  importProgress,
  setImportProgress,
  importResults,
  setImportResults,
  importSettings,
  setImportSettings,
}) => {
    const currentTheme = mo3tiThemes[theme] || mo3tiThemes.dark;
    // Employees whose personal info in the file differs from their record.
    const [personalChanges, setPersonalChanges] = useState([]);
    const [applyPersonalUpdates, setApplyPersonalUpdates] = useState(false);
    const [showPersonalChanges, setShowPersonalChanges] = useState(false);
    const isAr = language === "ar";
    const L = (ar, en) => (isAr ? ar : en);

    // ── F-2: Disclaimer acknowledgement ──────────────────────────────────────
    const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);

    // ── F-3 / F-1: Conflict state ─────────────────────────────────────────────
    // Map<clientCode, conflictInfo> — populated after /conflict-check
    const [conflicts, setConflicts] = useState({});
    // Map<clientCode, boolean> — auto-consent for STANDARD, explicit for sensitive
    const [overwriteConsent, setOverwriteConsent] = useState({});
    // Queue of sensitive conflicts waiting for user confirmation
    const [sensitiveDialogQueue, setSensitiveDialogQueue] = useState([]);
    const [currentDialog, setCurrentDialog] = useState(null);
    // Ref to resolve the per-row confirmation promise
    const dialogResolveRef = useRef(null);

    const columnMapping = {
      "Client Code": "clientCode",
      "Employee Name": "employeeName",
      "Payroll Start date": "payrollStartDate",
      "Payroll End date": "payrollEndDate",
      "Working days": "workingDays",
      "Absence Days": "absenceDays",
      "Absence Days Value": "absenceDaysValue",
      "unpaid & deduction days": "unpaidDeductionDays",
      "unpaid & deduction value": "unpaidDeductionValue",
      "OverTime Days": "overtimeDays",
      "OverTime value": "overtimeValue",
      "Bonus Days": "bonusDays",
      "Bonus Value": "bonusValue",
      expenses: "expenses",
      "Penality Days": "penalityDays",
      "Penality Days Value": "penalityDaysValue",
      "25% Deduction (Sick Leave) Days": "sickLeave25DeductionDays",
      "25% Deduction (Sick Leave) Value": "sickLeave25DeductionValue",
      Achievement: "achievement",
      "Actual Net Salary": "netSalary",
      Basic: "basicSalary",
    };

    // Headers are matched regardless of capitals and spacing, so the payroll
    // template's own spelling ("Payroll Start Date", "Working Days",
    // "OverTime Value", "Expenses", "Unpaid & Deduction Days", ...) is read
    // exactly like the original lower-case spellings above.
    const normHeader = (h) => String(h ?? "").replace(/\s+/g, " ").trim().toLowerCase();
    // Other spellings of the same column (normalized) -> columnMapping key.
    const HEADER_ALIASES = {
      "basic salary": "Basic",
    };
    // Local calendar date as YYYY-MM-DD. toISOString() converted local
    // midnight to UTC first, which in Egypt (UTC+2/+3) gave the PREVIOUS day —
    // a 1 Oct start date became 30 Sep and the row landed in the wrong month.
    const toLocalYmd = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    // "1,234.50" / "EGP 1,234" → 1234.5 (formatted cells arrive as text).
    const toNum = (v) => {
      if (typeof v === "number") return v;
      const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
      return Number.isFinite(n) ? n : 0;
    };
    const toYmdCell = (v) => {
      if (v === undefined || v === null || v === "") return null;
      if (typeof v === "number") {
        const d = new Date(new Date(1899, 11, 30).getTime() + v * 86400000);
        return toLocalYmd(d);
      }
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : toLocalYmd(d);
    };
    const knownMappingHeaders = new Set([
      ...Object.keys(columnMapping).map((h) => normHeader(h)),
      ...Object.keys(HEADER_ALIASES),
    ]);
    const ignoredHeaders = new Set(PAYROLL_IGNORED_HEADERS);

    // Everything beyond the original columns: personal info, payroll values,
    // allowance/deduction (Net)/(Gross) columns and any other column.
    const classifyRest = (rawRow, mapped) => {
      const personal = {};
      const adColumns = {};
      const importedValues = {};
      Object.entries(rawRow).forEach(([header, value]) => {
        if (value === undefined || value === null || String(value).trim() === "") return;
        const n = normHeader(header);
        if (knownMappingHeaders.has(n) || ignoredHeaders.has(n)) return;
        const personalCol = PERSONAL_BY_HEADER.get(n);
        if (personalCol) {
          if (!personalCol.identity) {
            personal[personalCol.key] = personalCol.type === "date" ? toYmdCell(value) : String(value).trim();
          }
          return;
        }
        const valueCol = VALUE_BY_HEADER.get(n);
        if (valueCol) {
          if (valueCol.field) {
            if (valueCol.type === "text") {
              mapped[valueCol.field] =
                valueCol.field === "paymentStatus" ? String(value).trim().toLowerCase() : String(value).trim();
            } else if (valueCol.type === "date") {
              const ymd = toYmdCell(value);
              if (ymd) mapped[valueCol.field] = ymd;
            } else if (valueCol.type === "int") {
              mapped[valueCol.field] = Math.round(toNum(value));
            } else {
              mapped[valueCol.field] = toNum(value);
            }
          } else {
            importedValues[valueCol.key] = toNum(value);
          }
          return;
        }
        if (isAllowanceDeductionHeader(header)) {
          adColumns[header] = toNum(value);
          return;
        }
        const numeric = typeof value === "number" || /^[\s\d,.\-]+$/.test(String(value));
        importedValues[String(header).trim()] = numeric ? toNum(value) : String(value).trim();
      });
      if (Object.keys(personal).length) mapped.personal = personal;
      if (Object.keys(adColumns).length) mapped.adColumns = adColumns;
      if (Object.keys(importedValues).length) mapped.importedValues = importedValues;
    };

    const readCell = (rowByHeader, excelCol) => {
      const direct = rowByHeader[normHeader(excelCol)];
      if (direct !== undefined && direct !== null && direct !== "") return direct;
      for (const [alias, target] of Object.entries(HEADER_ALIASES)) {
        if (target !== excelCol) continue;
        const v = rowByHeader[alias];
        if (v !== undefined && v !== null && v !== "") return v;
      }
      return direct;
    };

    const handleFileSelect = async (event) => {
      const input = event.target;
      const file = input.files?.[0];
      // Clear the input so choosing the same file again (after Back, or for a
      // second import) still fires onChange.
      input.value = "";
      if (!file) return;

      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls|csv)$/i)
      ) {
        setImportError(
          L("يرجى رفع ملف Excel صالح (xlsx أو xls أو csv)", "Please upload a valid Excel file (.xlsx, .xls, or .csv)"),
        );
        return;
      }

      setImportFile(file);
      setImportError(null);
      setImportLoading(true);

      try {
        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
              raw: false,
              defval: null,
            });

            console.log("📊 Parsed Excel data:", jsonData);

            if (jsonData.length === 0) {
              throw new Error(L("ملف Excel فارغ أو لا يحتوي على بيانات صالحة", "Excel file is empty or has no valid data"));
            }

            const transformedData = jsonData
              .map((rawRow, index) => ({ rawRow, index }))
              // The template's legend lines only fill the first column — skip
              // rows that carry nothing besides one cell.
              .filter(({ rawRow }) =>
                Object.values(rawRow).filter((v) => v !== null && v !== undefined && String(v).trim() !== "").length > 1,
              )
              .map(({ rawRow, index }) => {
              const mapped = {};
              const rowByHeader = {};
              Object.keys(rawRow).forEach((k) => {
                rowByHeader[normHeader(k)] = rawRow[k];
              });

              Object.keys(columnMapping).forEach((excelCol) => {
                const dbField = columnMapping[excelCol];
                const cellValue = readCell(rowByHeader, excelCol);
                if (
                  cellValue !== undefined &&
                  cellValue !== null &&
                  cellValue !== ""
                ) {
                  if (
                    [
                      "workingDays",
                      "absenceDays",
                      "unpaidDeductionDays",
                      "overtimeDays",
                      "bonusDays",
                      "penalityDays",
                      "sickLeave25DeductionDays",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = Math.round(toNum(cellValue));
                  } else if (
                    [
                      "absenceDaysValue",
                      "unpaidDeductionValue",
                      "overtimeValue",
                      "bonusValue",
                      "expenses",
                      "penalityDaysValue",
                      "sickLeave25DeductionValue",
                      "achievement",
                      "netSalary",
                      "basicSalary",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = toNum(cellValue);
                  } else if (
                    ["payrollStartDate", "payrollEndDate"].includes(dbField)
                  ) {
                    const dateValue = cellValue;
                    if (dateValue) {
                      if (typeof dateValue === "number") {
                        const excelEpoch = new Date(1899, 11, 30);
                        const date = new Date(
                          excelEpoch.getTime() + dateValue * 86400000,
                        );
                        mapped[dbField] = toLocalYmd(date);
                      } else {
                        const date = new Date(dateValue);
                        if (!isNaN(date)) {
                          mapped[dbField] = toLocalYmd(date);
                        }
                      }
                    }
                  } else {
                    mapped[dbField] = String(cellValue);
                  }
                }
              });

              classifyRest(rawRow, mapped);

              if (mapped.payrollStartDate) {
                const [y, mo] = mapped.payrollStartDate.split("-").map(Number);
                mapped.year = y;
                mapped.month = mo;
              } else {
                mapped.year = importSettings.defaultYear;
                mapped.month = importSettings.defaultMonth;
              }

              const errors = [];
              if (!mapped.clientCode) {
                errors.push(L("كود الموظف غير موجود", "Missing Client Code"));
              }
              if (!mapped.year || !mapped.month) {
                errors.push(L("فترة الرواتب غير موجودة أو غير صحيحة", "Missing or invalid payroll period"));
              }

              return {
                rowNumber: index + 2,
                data: mapped,
                errors: errors,
                isValid: errors.length === 0,
              };
            });

            console.log("✅ Transformed data:", transformedData);

            // Payroll import never creates employees, and never changes their
            // personal info without asking — check both before the preview.
            setPersonalChanges([]);
            setApplyPersonalUpdates(false);
            // Conflicts from a previous file must not carry over to this one.
            setConflicts({});
            setOverwriteConsent({});
            try {
              const validForCheck = transformedData.filter((r) => r.isValid);
              if (validForCheck.length) {
                // ── personal-diff ────────────────────────────────────────────
                const diffRes = await apiFetch(`${API_BASE_URL}/payrollX/import/excel/personal-diff`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    records: validForCheck.map((r) => ({ clientCode: r.data.clientCode, personal: r.data.personal || {} })),
                    clientName: localStorage.getItem("clientName") || undefined,
                  }),
                });
                if (diffRes.ok) {
                  const diffJson = await diffRes.json();
                  const missing = new Set((diffJson?.data?.missing || []).map(String));
                  transformedData.forEach((r) => {
                    if (r.isValid && missing.has(String(r.data.clientCode))) {
                      r.errors.push(L("الموظف غير موجود — أضف الموظف من إدارة الموظفين أولاً", "Employee not found — add the employee in Employee Management first"));
                      r.isValid = false;
                    }
                  });
                  setPersonalChanges(diffJson?.data?.changes || []);
                }

                // ── conflict-check ───────────────────────────────────────────
                // Re-filter after personal-diff may have invalidated some rows.
                const stillValid = transformedData.filter((r) => r.isValid);
                if (stillValid.length) {
                  try {
                    const conflictRes = await apiFetch(
                      `${API_BASE_URL}/payrollX/import/excel/conflict-check`,
                      {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          records: stillValid.map((r) => ({
                            clientCode: r.data.clientCode,
                            year: r.data.year,
                            month: r.data.month,
                          })),
                          clientName: localStorage.getItem("clientName") || undefined,
                        }),
                      }
                    );
                    if (conflictRes.ok) {
                      const conflictJson = await conflictRes.json();
                      const conflictList = conflictJson?.data?.conflicts || [];
                      // Build a Map<clientCode, conflictInfo>
                      const conflictMap = {};
                      const autoConsent = {};
                      conflictList.forEach((c) => {
                        conflictMap[c.clientCode] = c;
                        // STANDARD rows are auto-consented — user sees the badge
                        // but no dialog is shown.
                        if (c.conflictType === "STANDARD") {
                          autoConsent[c.clientCode] = true;
                        }
                      });
                      setConflicts(conflictMap);
                      setOverwriteConsent(autoConsent);
                    }
                  } catch (conflictErr) {
                    console.warn("Conflict-check failed (non-fatal):", conflictErr);
                  }
                }
              }
            } catch (diffErr) {
              console.warn("Personal-info check failed:", diffErr);
            }

            setImportData(transformedData);
            setImportStep(2);
          } catch (parseError) {
            console.error("Parse error:", parseError);
            setImportError(`${L("تعذّر قراءة ملف Excel", "Failed to parse Excel file")}: ${parseError.message}`);
          } finally {
            setImportLoading(false);
          }
        };

        reader.onerror = () => {
          setImportError(L("تعذّر قراءة الملف", "Failed to read file"));
          setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error("File processing error:", error);
        setImportError(`${L("خطأ أثناء معالجة الملف", "Error processing file")}: ${error.message}`);
        setImportLoading(false);
      }
    };

    // ── Prompt helper: returns a promise that resolves to true (overwrite)
    // or false (skip) once the user clicks in SensitiveOverwriteDialog.
    const promptSensitive = (conflict) =>
      new Promise((resolve) => {
        dialogResolveRef.current = resolve;
        setCurrentDialog(conflict);
      });

    const handleDialogSkip = () => {
      const resolve = dialogResolveRef.current;
      setCurrentDialog(null);
      dialogResolveRef.current = null;
      if (resolve) resolve(false);
    };

    const handleDialogOverwrite = () => {
      const resolve = dialogResolveRef.current;
      setCurrentDialog(null);
      dialogResolveRef.current = null;
      if (resolve) resolve(true);
    };

    const handleImport = async () => {
      if (!importData || importData.length === 0) {
        setImportError(L("لا توجد بيانات للاستيراد", "No data to import"));
        return;
      }

      setImportLoading(true);
      setImportProgress(0);
      setImportError(null);
      setImportResults(null);

      const results = {
        total: importData.length,
        successful: 0,
        failed: 0,
        skipped: 0,
        errors: [],
      };

      try {
        const validRecords = importData
          .filter((row) => row.isValid)
          .map((row) => row.data);

        const invalidCount = importData.length - validRecords.length;
        results.skipped = invalidCount;

        if (validRecords.length === 0) {
          throw new Error(
            L(
              `لا توجد سجلات صالحة للاستيراد (${invalidCount} سجل لم يجتز التحقق)`,
              `No valid records to import (${invalidCount} records failed validation)`,
            ),
          );
        }

        // ── Build overwriteClientCodes ────────────────────────────────────
        // Start with all auto-consented (STANDARD) codes, then ask for each
        // PAID / HAS_REPAYMENT / PAID_AND_REPAYMENT row.
        const finalConsentMap = { ...overwriteConsent };

        const sensitiveTypes = new Set(["PAID", "HAS_REPAYMENT", "PAID_AND_REPAYMENT"]);
        for (const record of validRecords) {
          const conflict = conflicts[record.clientCode];
          if (!conflict) continue;                  // no conflict → no overwrite needed
          if (!sensitiveTypes.has(conflict.conflictType)) continue; // STANDARD already auto-consented
          if (finalConsentMap[record.clientCode] !== undefined) continue; // already decided

          setImportLoading(false); // pause spinner while dialog is open
          const userChoseOverwrite = await promptSensitive(conflict);
          setImportLoading(true);
          finalConsentMap[record.clientCode] = userChoseOverwrite;
        }

        // Filter out rows the user chose to skip
        const acceptedRecords = validRecords.filter((record) => {
          const conflict = conflicts[record.clientCode];
          if (!conflict) return true;  // new row — always import
          return finalConsentMap[record.clientCode] !== false;
        });
        const skippedByUser = validRecords.length - acceptedRecords.length;
        results.skipped += skippedByUser;

        if (acceptedRecords.length === 0) {
          throw new Error(L("لا توجد سجلات للاستيراد بعد حل التعارضات.", "No records to import after conflict resolution."));
        }

        // Build the list of client codes the user explicitly consented to overwrite
        const overwriteClientCodes = acceptedRecords
          .filter((r) => conflicts[r.clientCode])   // only rows that have a conflict
          .map((r) => r.clientCode);

        console.log(`Importing ${acceptedRecords.length} valid records (${overwriteClientCodes.length} overwrites)...`);

        setImportProgress(20);

        const response = await apiFetch(
          `${API_BASE_URL}/payrollX/import/excel`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              payrollRecords: acceptedRecords,
              // Personal info is only written after the user ticked the box
              // in the preview (never by default, never salary fields).
              applyPersonalUpdates,
              // Explicitly consented overwrite codes
              overwriteClientCodes,
              // The user accepted the imported-values disclaimer (Upload step);
              // the server refuses the import without it.
              acknowledgeImportDisclaimer: disclaimerAcknowledged === true,
              // Only honored by the backend for superadmins; everyone else
              // is pinned to their own client.
              clientName: localStorage.getItem("clientName") || undefined,
            }),
            timeout: 300000,
          },
        );

        setImportProgress(80);

        if (!response.ok) {
          let errorData;
          try {
            const contentType = response.headers.get("content-type");
            errorData =
              contentType && contentType.includes("application/json")
                ? await response.json()
                : await response.text();
          } catch {
            errorData = `HTTP ${response.status}: ${response.statusText}`;
          }

          const errorMessage =
            errorData?.error || errorData?.message || errorData;
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (!result.data) {
          throw new Error(L("استجابة غير صالحة من الخادم", "Invalid response format from server"));
        }

        results.successful = result.data.successful || 0;
        results.failed = result.data.failed || 0;
        results.total = result.data.total || validRecords.length;
        results.errors = result.data.errors || [];
        results.created = result.data.created || 0;
        results.replaced = result.data.replaced ?? result.data.overwritten ?? 0;
        // Existing payrolls the server kept (replacing them was not approved,
        // or their state changed after the preview).
        results.kept = Array.isArray(result.data.skipped) ? result.data.skipped : [];
        results.skipped += results.kept.length;
        results.warnings = result.data.warnings || [];

        console.log("Import completed:", results);

        setImportResults(results);
        setImportStep(3);
        setImportProgress(100);

        if (results.failed > 0) {
          console.warn(
            `Import completed with ${results.failed} failures. Check errors for details.`,
          );
        }
      } catch (error) {
        console.error("Import error:", error);

        const errorMessage =
          error.message || L("حدث خطأ غير متوقع أثناء الاستيراد", "An unexpected error occurred during import");

        setImportError(errorMessage);
        setImportResults({
          ...results,
          successful: 0,
          failed: results.total,
        });

        setImportStep(2);
      } finally {
        setImportLoading(false);
        setImportProgress(100);
      }
    };

    const modalStyles = {
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        backdropFilter: "blur(4px)",
      },
      modal: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 1.6)"
            : "rgba(255, 255, 255, 0.9)",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "16px",
        width: "100%",
        maxWidth: "1100px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        //boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "24px 32px",
        borderBottom: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "24px",
        fontWeight: "700",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      closeButton: {
        width: "40px",
        height: "40px",
        flexShrink: 0,
        background: "transparent",
        border: "none",
        borderRadius: "10px",
        cursor: "pointer",
        color: currentTheme.colors.textSecondary,
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      content: {
        padding: "32px",
        overflowY: "auto",
        flex: 1,
      },
      footer: {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
        padding: "20px 32px",
        borderTop: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      uploadArea: {
        border: `2px dashed ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "48px 32px",
        textAlign: "center",
        background: `${currentTheme.colors.primary}05`,
        cursor: "pointer",
        transition: "all 0.3s ease",
        outline: "none",
        display: "block",
      },
      statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 4fr))",
        gap: "16px",
        marginBottom: "24px",
      },
      statCard: {
        padding: "20px",
        background: currentTheme.colors.inputBackground,
        borderRadius: "12px",
        border: `1px solid ${currentTheme.colors.border}`,
        textAlign: "center",
      },
      statValue: {
        fontSize: "32px",
        fontWeight: "700",
        marginBottom: "8px",
      },
      statLabel: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "500",
      },
      infoBox: {
        padding: "16px",
        background: `${currentTheme.colors.info}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.info}30`,
        marginTop: "24px",
      },
      warningBox: {
        padding: "16px",
        background: `${currentTheme.colors.warning}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.warning}30`,
        marginTop: "16px",
      },
      errorBox: {
        padding: "16px",
        background: `${currentTheme.colors.danger}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.danger}30`,
        marginTop: "16px",
      },
      successBox: {
        padding: "16px",
        background: `${currentTheme.colors.success}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.success}30`,
        marginBottom: "16px",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
        background: currentTheme.colors.cardBackground,
      },
      tableContainer: {
        maxHeight: "400px",
        overflowY: "auto",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "10px",
        marginTop: "16px",
      },
    };
    const enhancedTableStyles = (currentTheme, isRTL) => ({
      tableContainer: {
        overflowX: "auto",
        borderRadius: "20px",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        background: currentTheme.colors.cardBackground,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      },
      table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0",
        fontSize: "14px",
        direction: isRTL ? "rtl" : "ltr",
      },
      thead: {
        background: `linear-gradient(135deg, ${currentTheme.colors.primary}00, ${currentTheme.colors.primary}05)`,
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      th: {
        padding: "20px 16px",
        textAlign: "start",
        color: currentTheme.colors.textPrimary,
        fontWeight: "800",
        fontSize: "13px",
        textTransform: "uppercase",
        letterSpacing: "1.2px",
        borderBottom: `3px solid ${currentTheme.colors.primary}40`,
        whiteSpace: "nowrap",
        background: currentTheme.colors.inputBackground,
      },
      thCenter: {
        textAlign: "center",
      },
      tr: {
        background: currentTheme.colors.cardBackground,
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
      },
      td: {
        padding: "20px 16px",
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        verticalAlign: "middle",
      },
      employeeCell: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
      },
      employeeName: {
        color: currentTheme.colors.textPrimary,
        fontSize: "16px",
        fontWeight: "700",
        marginBottom: "4px",
      },
      employeeDepartment: {
        color: currentTheme.colors.textSecondary,
        fontSize: "13px",
      },
      employeePosition: {
        color: currentTheme.colors.textMuted,
        fontSize: "12px",
      },
      salaryValue: {
        color: currentTheme.colors.textPrimary,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      allowanceValue: {
        color: currentTheme.colors.success,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      deductionValue: {
        color: currentTheme.colors.danger,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      netSalaryValue: {
        color: currentTheme.colors.success,
        fontSize: "18px",
        fontWeight: "900",
        fontFamily: "monospace",
        textShadow: `0 2px 4px ${currentTheme.colors.success}30`,
      },
      subText: {
        fontSize: "11px",
        color: currentTheme.colors.textMuted,
        marginTop: "4px",
      },
      actionButtons: {
        display: "flex",
        gap: "8px",
        justifyContent: "center",
      },
    });

    const renderStepContent = () => {
      switch (importStep) {
        case 1:
          return (
            <UploadStep
              t={t}
              currentTheme={currentTheme}
              modalStyles={modalStyles}
              importFile={importFile}
              importError={importError}
              importLoading={importLoading}
              importSettings={importSettings}
              setImportSettings={setImportSettings}
              handleFileSelect={handleFileSelect}
              disclaimerAcknowledged={disclaimerAcknowledged}
              setDisclaimerAcknowledged={setDisclaimerAcknowledged}
              isAr={isAr}
            />
          );

        case 2:
          return (
            <PreviewStep
              t={t}
              currentTheme={currentTheme}
              modalStyles={modalStyles}
              importData={importData}
              conflicts={conflicts}
              personalChanges={personalChanges}
              applyPersonalUpdates={applyPersonalUpdates}
              setApplyPersonalUpdates={setApplyPersonalUpdates}
              showPersonalChanges={showPersonalChanges}
              setShowPersonalChanges={setShowPersonalChanges}
              isAr={isAr}
            />
          );

        case 3:
          return (
            <ResultsStep
              t={t}
              currentTheme={currentTheme}
              modalStyles={modalStyles}
              importResults={importResults}
              isAr={isAr}
            />
          );

        default:
          return null;
      }
    };

    return (
      <div
        className="payroll-modal"
        style={modalStyles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={modalStyles.modal}>
          {/* Header */}
          <div style={modalStyles.header}>
            <h1 style={modalStyles.title}>
              <span
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  background: `${currentTheme.colors.primary}1f`,
                  color: currentTheme.colors.primary,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FileSpreadsheet size={22} />
              </span>
              {t("importPayroll")}
            </h1>
            <button
              type="button"
              className="xi-close"
              style={modalStyles.closeButton}
              onClick={onClose}
              disabled={importLoading}
              aria-label={t("close")}
            >
              <X size={22} />
            </button>
          </div>

          {/* Step Indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "20px 32px",
              borderBottom: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
            }}
          >
            {[
              { number: 1, titleKey: "upload", icon: Upload },
              { number: 2, titleKey: "preview", icon: Eye },
              { number: 3, titleKey: "results", icon: CheckCircle },
            ].map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    background:
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : "transparent",
                    color:
                      importStep >= step.number
                        ? "#fff"
                        : currentTheme.colors.textSecondary,
                    border: `1px solid ${
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : currentTheme.colors.border
                    }`,
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "700",
                      background:
                        importStep >= step.number
                          ? "#fff"
                          : currentTheme.colors.border,
                      color:
                        importStep >= step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.textSecondary,
                    }}
                  >
                    {step.number}
                  </div>
                  <step.icon size={16} />
                  {t(step.titleKey)}
                </div>
                {index < 2 && (
                  <div
                    style={{
                      width: "40px",
                      height: "2px",
                      background:
                        importStep > step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.border,
                      transition: "all 0.3s ease",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress Bar */}
          {importLoading && importProgress > 0 && (
            <div
              style={{
                padding: "16px 32px",
                background: currentTheme.colors.inputBackground,
                borderBottom: `1px solid ${currentTheme.colors.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    color: currentTheme.colors.textPrimary,
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  {L("جارٍ استيراد سجلات الرواتب...", "Importing payroll records...")}
                </span>
                <span
                  style={{
                    color: currentTheme.colors.primary,
                    fontWeight: "700",
                    fontSize: "14px",
                  }}
                >
                  {importProgress}%
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  background: currentTheme.colors.border,
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${importProgress}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${currentTheme.colors.primary}, ${currentTheme.colors.success})`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div style={modalStyles.content}>{renderStepContent()}</div>

          {/* Footer */}
          <div style={modalStyles.footer}>
            {importStep === 2 && (
              <>
                <Mo3tiButton
                  variant="secondary"
                  onClick={() => {
                    setImportStep(1);
                    setImportData(null);
                  }}
                  disabled={importLoading}
                  icon={isAr ? ArrowRight : ArrowLeft}
                >
                  {t("back")}
                </Mo3tiButton>
                <Mo3tiButton
                  variant="primary"
                  icon={Upload}
                  onClick={handleImport}
                  disabled={
                    importLoading || !importData || importData.length === 0
                  }
                >
                  {importLoading
                    ? L("جارٍ الاستيراد...", "Importing...")
                    : L(
                        `استيراد ${importData?.filter((r) => r.isValid).length || 0} سجل`,
                        `Import ${importData?.filter((r) => r.isValid).length || 0} Records`,
                      )}
                </Mo3tiButton>
              </>
            )}

            {importStep === 3 && (
              <Mo3tiButton
                variant="primary"
                icon={CheckCircle}
                onClick={() => {
                  onImportComplete();
                }}
              >
                {L("تم", "Done")}
              </Mo3tiButton>
            )}
          </div>
        </div>

        <style>{`.xi-close:not(:disabled):hover { background: ${currentTheme.colors.danger}14 !important; color: ${currentTheme.colors.danger} !important; }
          .xi-close:disabled { opacity: 0.4; cursor: not-allowed !important; }`}</style>

        {/* Sensitive Overwrite Confirmation Dialog */}
        {currentDialog && (
          <SensitiveOverwriteDialog
            conflict={currentDialog}
            onSkip={handleDialogSkip}
            onOverwrite={handleDialogOverwrite}
            currentTheme={currentTheme}
            isAr={isAr}
          />
        )}
      </div>
    );
  };

export default ExcelImportModal;